import type { GeoJSONPoint } from '../api/types';
import { scoreShape, type ScoreShape } from '../format/score';
import { fromCoordinates } from '../geo/geoPoint';
import { planStaticImage, resolveImagerySource } from '../geo/imagery';
import { TILE_SIZE, tileContaining, type TileCoord } from '../geo/slippyMap';
import { t, type PlainMessageKey } from '../i18n';

/**
 * The share cards (canvas 1i), drawn to a `<canvas>` so they can be downloaded as real
 * images at the sizes the platforms actually want: 1080×1080 for a feed, 1080×1920 for a
 * story.
 *
 * The canvas's own note on the design: "Sem foto, sem confete: a silhueta do campo é o
 * gráfico. O dourado marca o resultado contra o par — um por cartão." So the aerial view is
 * desaturated to a texture under the type, and the only saturated colour on the card is the
 * gold on the score relative to par.
 *
 * Everything below is expressed in the canvas's own reference units (a 420pt square, a 280pt
 * story) and scaled once on the way out. That way the numbers here can be compared with the
 * design line by line instead of being pre-multiplied into something unrecognisable.
 */

export type ShareFormat = 'square' | 'story';

/** One column of the hole-by-hole strip. */
export interface ShareHole {
  /** Logical hole number, 1..holesPlayed. */
  number: number;
  strokes: number;
  par: number;
}

export interface ShareCardData {
  courseName: string;
  /** "19 jul 2026" */
  dateLabel: string;
  /** "Tee blue" */
  teeLabel: string;
  strokes: number | null;
  toPar: number | null;
  putts: number | null;
  girPct: number | null;
  fairwayPct: number | null;
  differential: number | null;
  /**
   * The played holes, in order. Empty is a valid state — a round with no scores simply gets
   * no strip, and the rest of the card closes up around the gap.
   */
  holes: ShareHole[];
  location?: GeoJSONPoint | null;
}

interface FormatSpec {
  width: number;
  height: number;
  /** Design-unit width the canvas was drawn at. */
  ref: number;
  background: string;
}

export const SHARE_FORMATS: Record<ShareFormat, FormatSpec> = {
  square: { width: 1080, height: 1080, ref: 420, background: '#14472F' },
  story: { width: 1080, height: 1920, ref: 280, background: '#0B110D' },
};

export const SHARE_LABEL: Record<ShareFormat, PlainMessageKey> = {
  square: 'share.format.square',
  story: 'share.format.story',
};

const UI = "'Archivo', system-ui, sans-serif";
const DISPLAY = "'Newsreader', Georgia, serif";

/**
 * The canvas's palette for this surface, transcribed from 1i.
 *
 * `MUTED` is `--on-green-muted`, not `--on-ink-muted`: 1i writes `#B9C2BB` for every eyebrow and
 * label on **both** cards. It was `#9BA49D` here, which is the ink variant — one step darker, and
 * on the square card's `#14472F` that is the difference between a label that recedes and one that
 * disappears.
 */
const MUTED = '#B9C2BB';
const ON_INK = '#F4F3EE';
const GOLD = '#F0C862';
const WORDMARK = '#CFE0D5';

/** A arte reversa da marca e a altura com que o canvas a assina (1i). */
const WORDMARK_SRC = '/brand/pintrack-wordmark-reverse.png';
const WORDMARK_H = 17;

// ------------------------------------------------------------------ drawing

interface TextOptions {
  size: number;
  weight?: number | string;
  family?: string;
  color?: string;
  /** In em, as the canvas writes it (.16em on the eyebrow). */
  tracking?: number;
  align?: 'left' | 'right' | 'center';
  /**
   * 'top' places `y` at the top of the line; 'alphabetic' at the baseline; 'middle' at the
   * optical centre, which is what the score marks need — the glyph has to sit in the ring.
   */
  baseline?: 'top' | 'alphabetic' | 'middle';
}

/**
 * A pen that speaks design units. `k` is the single scale factor between the canvas file and
 * the exported image, applied at the last possible moment.
 */
class Pen {
  constructor(
    private ctx: CanvasRenderingContext2D,
    private k: number,
  ) {}

  private applyFont(o: TextOptions) {
    const family = o.family ?? UI;
    this.ctx.font = `${o.weight ?? 400} ${o.size * this.k}px ${family}`;
    this.ctx.fillStyle = o.color ?? ON_INK;
    this.ctx.textBaseline = o.baseline ?? 'top';
    this.ctx.textAlign = 'left';
  }

  /** Width of `text` in design units, tracking included. */
  measure(text: string, o: TextOptions): number {
    this.applyFont(o);
    const base = this.ctx.measureText(text).width / this.k;
    return base + (o.tracking ?? 0) * o.size * text.length;
  }

  text(text: string, x: number, y: number, o: TextOptions): void {
    this.applyFont(o);

    const width = this.measure(text, o);
    const startX =
      o.align === 'right' ? x - width : o.align === 'center' ? x - width / 2 : x;

    if (!o.tracking) {
      this.ctx.fillText(text, startX * this.k, y * this.k);
      return;
    }

    /*
     * `ctx.letterSpacing` is recent (Chrome 99, Safari 17.4). Where it is missing the
     * eyebrow would collapse to normal spacing and lose a signature of the design, so the
     * fallback lays the glyphs out by hand rather than giving up the tracking.
     */
    // Read into a boolean first: the DOM lib declares `letterSpacing` unconditionally, so an
    // inline `in` check narrows the fallback branch to `never` and TypeScript rejects it.
    const supportsTracking = typeof this.ctx.letterSpacing === 'string';
    if (supportsTracking) {
      this.ctx.letterSpacing = `${o.tracking * o.size * this.k}px`;
      this.ctx.fillText(text, startX * this.k, y * this.k);
      this.ctx.letterSpacing = '0px';
      return;
    }

    let cursor = startX;
    for (const ch of text) {
      this.ctx.fillText(ch, cursor * this.k, y * this.k);
      cursor += this.ctx.measureText(ch).width / this.k + o.tracking * o.size;
    }
  }

  /**
   * Draws an image sized by height, keeping its own aspect ratio — the wordmark is art, and
   * the card must not decide how wide it is. `align: 'right'` anchors `x` to the right edge,
   * `bottom: true` anchors `y` to the baseline, so both call sites keep the coordinates the
   * text version used.
   */
  image(
    img: HTMLImageElement,
    x: number,
    y: number,
    height: number,
    o: { align?: 'left' | 'right'; bottom?: boolean } = {},
  ): void {
    const width = height * (img.naturalWidth / img.naturalHeight);
    const drawX = o.align === 'right' ? x - width : x;
    const drawY = o.bottom ? y - height : y;
    this.ctx.drawImage(img, drawX * this.k, drawY * this.k, width * this.k, height * this.k);
  }

  /** Greedy word wrap. Returns the lines actually drawn. */
  paragraph(text: string, x: number, y: number, maxWidth: number, lineHeight: number, o: TextOptions): number {
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let line = '';

    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (line && this.measure(candidate, o) > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    }
    if (line) lines.push(line);

    lines.forEach((l, i) => this.text(l, x, y + i * lineHeight, o));
    return lines.length;
  }

  rect(x: number, y: number, w: number, h: number, fill: string): void {
    this.ctx.fillStyle = fill;
    this.ctx.fillRect(x * this.k, y * this.k, w * this.k, h * this.k);
  }

  /** Stroked circle centred on (x, y) — the birdie ring of the score notation. */
  circle(x: number, y: number, radius: number, color: string, width: number): void {
    this.ctx.beginPath();
    this.ctx.arc(x * this.k, y * this.k, radius * this.k, 0, Math.PI * 2);
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = width * this.k;
    this.ctx.stroke();
  }

  /** Stroked square centred on (x, y) — the bogey mark. */
  square(x: number, y: number, size: number, color: string, width: number): void {
    const half = size / 2;
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = width * this.k;
    this.ctx.strokeRect((x - half) * this.k, (y - half) * this.k, size * this.k, size * this.k);
  }
}

// ------------------------------------------------------------------ imagery

/**
 * An aerial view of the course, as an offscreen canvas, or null when there is none to be had.
 *
 * Uses whatever imagery source the app is configured with, so switching provider switches
 * the card too. `crossOrigin` is mandatory: without it the drawn image taints the canvas and
 * `toBlob` throws, which would turn "download" into a silent no-op.
 */
async function loadCourseImage(location: GeoJSONPoint | null | undefined): Promise<HTMLCanvasElement | null> {
  const coords = location?.coordinates;
  if (!coords || !Number.isFinite(coords[0]) || !Number.isFinite(coords[1])) return null;

  const point = fromCoordinates([coords[0], coords[1]]);
  const { source } = resolveImagerySource();

  if (source.kind === 'static') {
    // One request, sized square — the card crops it to whichever shape it needs.
    const span = 0.004;
    const plan = planStaticImage(
      {
        minLongitude: point.longitude - span,
        maxLongitude: point.longitude + span,
        minLatitude: point.latitude - span,
        maxLatitude: point.latitude + span,
      },
      source.maxLogicalSize,
      source.maxLogicalSize,
      source.maxLogicalSize,
    );
    const img = await loadImage(source.staticUrl(plan.request));
    if (!img) return null;
    return toCanvas([{ img, x: 0, y: 0 }], img.width, img.height);
  }

  /*
   * A 3×3 tile block around the course: 768px of source for an image that will be printed
   * at ~1120. One tile upscaled 4× would be visible even through the grayscale and the 50%
   * overlay; three tiles is enough that the texture reads as terrain.
   */
  const centre = tileContaining(point, 16);
  const coordsList: TileCoord[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      coordsList.push({ z: centre.z, x: centre.x + dx, y: centre.y + dy });
    }
  }

  const loaded = await Promise.all(
    coordsList.map(async (t) => {
      const img = await loadImage(source.tileUrl(t));
      return img
        ? { img, x: (t.x - centre.x + 1) * TILE_SIZE, y: (t.y - centre.y + 1) * TILE_SIZE }
        : null;
    }),
  );

  const placed = loaded.filter((v): v is { img: HTMLImageElement; x: number; y: number } => v != null);
  // A couple of missing edge tiles is survivable; a mostly-empty grid is not a texture.
  if (placed.length < 5) return null;
  return toCanvas(placed, TILE_SIZE * 3, TILE_SIZE * 3);
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function toCanvas(
  parts: Array<{ img: HTMLImageElement; x: number; y: number }>,
  width: number,
  height: number,
): HTMLCanvasElement | null {
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  for (const p of parts) ctx.drawImage(p.img, p.x, p.y);
  return c;
}

/** `object-fit: cover` for a canvas source drawn into a destination rect. */
function drawCover(
  ctx: CanvasRenderingContext2D,
  src: HTMLCanvasElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
  focusY = 0.5,
): void {
  const scale = Math.max(dw / src.width, dh / src.height);
  const w = src.width * scale;
  const h = src.height * scale;
  ctx.drawImage(src, dx + (dw - w) / 2, dy + (dh - h) * focusY, w, h);
}

// ------------------------------------------------------------------- layout

function fmt1(n: number): string {
  return n.toFixed(1).replace('.', ',');
}

function toParLabel(toPar: number | null): string | null {
  if (toPar == null) return null;
  if (toPar === 0) return 'E';
  return toPar > 0 ? `+${toPar}` : `${toPar}`;
}

function statValue(n: number | null, suffix = ''): string {
  return n == null ? '—' : `${Math.round(n)}${suffix}`;
}

// -------------------------------------------------------------- score strip

/**
 * A tira de 18 buracos (1i).
 *
 * The canvas's own note: it "usa a mesma notação de forma do scorecard (1a), traduzida para
 * superfície escura", and it is what "transforma o cartão de placar em relato — quem vê entende
 * onde a volta foi ganha ou perdida sem precisar de legenda".
 *
 * **The shape carries the meaning, and here it carries it alone.** 1a's colours are picked for
 * paper: `--green-bright` reaches only 2.81:1 on the square card's `#14472F` and `--danger` a
 * flat 1.74:1 — a double bogey would be a smudge, and the strokes inside it unreadable. The
 * canvas asks for lighter shades ("verde claro", "terracota claro") but the strip is styled from
 * runtime data, so it names no hex, and `tokens.css` forbids inventing one. So the marks are
 * drawn in `--on-ink`, with the neutral bogey keeping its muted border, and the notation still
 * reads exactly as 1a specifies it: ring, square, doubled outline, or nothing at all. The day
 * those two shades land in the canvas, they are two constants below.
 */
interface StripStyle {
  /** Gap between every item in a row — label, nine cells, total. */
  gap: number;
  /** Width of the OUT/IN column and of the total column; both `flex: none` in 1i. */
  sideWidth: number;
  labelSize: number;
  /** Hole number above each mark. */
  numberSize: number;
  /** Gap between the hole number and the mark. */
  cellGap: number;
  /** Diameter of the ring, side of the square. */
  markSize: number;
  /** The strokes, drawn inside the mark. */
  markTextSize: number;
  totalSize: number;
  /** Gap between the OUT and IN rows. */
  rowGap: number;
  /** Space above the block's rule, and between the rule and the first row. */
  marginTop: number;
  paddingTop: number;
  divider: string;
  /** Ring width of the mark outline. */
  stroke: number;
  /** Distance between the two outlines of an eagle/double mark. */
  ringGap: number;
}

const SQUARE_STRIP: StripStyle = {
  gap: 4,
  sideWidth: 26,
  labelSize: 8.5,
  numberSize: 8,
  cellGap: 3,
  markSize: 20,
  markTextSize: 11,
  totalSize: 14,
  rowGap: 8,
  marginTop: 20,
  paddingTop: 16,
  divider: 'rgba(244,243,238,.22)',
  stroke: 1.1,
  ringGap: 2.2,
};

/**
 * The story's nine cells share 158 units, so each is 17.6 wide — and it is the **doubled**
 * outline that sets the mark's size, not the mark. At 14 with a 1.7 ring the outer square
 * measured 17.4 and a double bogey touched both its neighbours; 11.5 with a 1.5 ring lands at
 * 14.5 and leaves the column its air back. Rendered, that is still a 44 px mark on the 1080 px
 * export, so nothing is lost to legibility.
 */
const STORY_STRIP: StripStyle = {
  gap: 3,
  sideWidth: 20,
  labelSize: 8,
  numberSize: 7.5,
  cellGap: 2,
  markSize: 11.5,
  markTextSize: 7.5,
  totalSize: 12,
  rowGap: 7,
  marginTop: 14,
  paddingTop: 16,
  divider: 'rgba(244,243,238,.18)',
  stroke: 0.9,
  ringGap: 1.5,
};

interface StripRow {
  label: string;
  holes: ShareHole[];
}

/**
 * OUT / IN, or a single row for nine holes.
 *
 * Same split as `Scorecard`'s `segmentsFor`, and the same vocabulary: a 9-hole round has no
 * front and back, so its one row is the total — labelled `TOT`, as the scorecard labels the
 * equivalent column.
 */
function stripRows(holes: ShareHole[]): StripRow[] {
  const ordered = [...holes].sort((a, b) => a.number - b.number);
  if (ordered.length === 0) return [];
  if (ordered.length <= 9) return [{ label: 'TOT', holes: ordered }];
  const half = Math.floor(ordered.length / 2);
  return [
    { label: 'OUT', holes: ordered.slice(0, half) },
    { label: 'IN', holes: ordered.slice(half) },
  ];
}

function rowHeight(s: StripStyle): number {
  return s.numberSize * 1.2 + s.cellGap + s.markSize;
}

/**
 * From the block's rule down to the bottom of its last row. **Margins are the caller's**, both
 * above and below — each card spaces the strip against its own neighbours, and folding those
 * into the height is what makes an off-by-one here land as an overlap on the card.
 */
function stripHeight(s: StripStyle, rows: StripRow[]): number {
  if (rows.length === 0) return 0;
  return s.paddingTop + rows.length * rowHeight(s) + (rows.length - 1) * s.rowGap;
}

/**
 * One mark. `par` draws nothing but the number — "sem forma no par", and the absence *is* the
 * notation, which is why the legend on the scorecard has to spell it out and this card does not.
 */
function drawMark(pen: Pen, x: number, y: number, s: StripStyle, hole: ShareHole): void {
  const shape: ScoreShape = scoreShape(hole.strokes, hole.par);
  const half = s.markSize / 2;

  if (shape === 'birdie' || shape === 'eagle') {
    pen.circle(x, y, half, ON_INK, s.stroke);
    // Eagle-or-better doubles the outline rather than deepening the colour, so it survives
    // being read by someone who cannot separate the greens — 1a's rule.
    if (shape === 'eagle') pen.circle(x, y, half + s.ringGap, ON_INK, s.stroke);
  } else if (shape === 'bogey') {
    // Neutral on purpose: "um bogey não é um alarme".
    pen.square(x, y, s.markSize, MUTED, s.stroke);
  } else if (shape === 'double') {
    pen.square(x, y, s.markSize, ON_INK, s.stroke);
    pen.square(x, y, s.markSize + s.ringGap * 2, ON_INK, s.stroke);
  }

  pen.text(`${hole.strokes}`, x, y, {
    size: s.markTextSize,
    weight: 700,
    align: 'center',
    baseline: 'middle',
  });
}

/** Draws the block and returns nothing; `top` is the top of its rule. */
function drawStrip(
  pen: Pen,
  x: number,
  top: number,
  width: number,
  rows: StripRow[],
  s: StripStyle,
): void {
  if (rows.length === 0) return;

  pen.rect(x, top, width, 1, s.divider);

  /*
   * Nine cells at `flex: 1` between two fixed columns, with a gap between all eleven items.
   * Held to nine even on the IN row of a course whose halves are uneven, so the two rows stay
   * on the same grid — a strip whose columns do not line up stops reading as a card.
   */
  const cells = 9;
  const cellWidth = (width - 2 * s.sideWidth - (cells + 1) * s.gap) / cells;
  const rowH = rowHeight(s);

  rows.forEach((row, index) => {
    const rowTop = top + s.paddingTop + index * (rowH + s.rowGap);
    const markCentre = rowTop + s.numberSize * 1.2 + s.cellGap + s.markSize / 2;

    pen.text(row.label, x, rowTop + rowH / 2, {
      size: s.labelSize,
      color: MUTED,
      tracking: 0.12,
      baseline: 'middle',
    });

    row.holes.slice(0, cells).forEach((hole, i) => {
      const centre = x + s.sideWidth + s.gap + i * (cellWidth + s.gap) + cellWidth / 2;
      pen.text(`${hole.number}`, centre, rowTop, {
        size: s.numberSize,
        color: MUTED,
        align: 'center',
      });
      drawMark(pen, centre, markCentre, s, hole);
    });

    const total = row.holes.reduce((sum, h) => sum + h.strokes, 0);
    pen.text(`${total}`, x + width, markCentre, {
      size: s.totalSize,
      weight: 800,
      align: 'right',
      baseline: 'middle',
    });
  });
}

/**
 * Draws one card. `withImagery: false` re-renders on flat brand colour — the fallback for
 * imagery that cannot be exported (a provider that serves no CORS header taints the canvas,
 * and the tainting is only discovered when `toBlob` throws).
 */
export async function drawShareCard(
  canvas: HTMLCanvasElement,
  data: ShareCardData,
  format: ShareFormat,
  options: { withImagery?: boolean } = {},
): Promise<void> {
  const spec = SHARE_FORMATS[format];
  const k = spec.width / spec.ref;

  canvas.width = spec.width;
  canvas.height = spec.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  await ensureFonts();

  ctx.fillStyle = spec.background;
  ctx.fillRect(0, 0, spec.width, spec.height);

  const image = options.withImagery === false ? null : await loadCourseImage(data.location);
  if (image) drawImagery(ctx, image, format, spec);

  /*
   * O wordmark é imagem, então precisa estar carregado antes de desenhar — por isso ele entra
   * aqui e desce como argumento, em vez de ser buscado dentro do desenho, que é síncrono.
   * `null` não é falha fatal: `drawWordmark` cai para o texto e o cartão nunca sai sem marca.
   */
  const wordmark = await loadImage(WORDMARK_SRC);

  const pen = new Pen(ctx, k);
  if (format === 'square') drawSquare(pen, data, spec, wordmark);
  else drawStory(pen, data, spec, wordmark);
}

/**
 * A marca no rodapé do cartão. O canvas a desenha com 17 unidades de altura; se o arquivo não
 * carregar, o nome sai na serifa do app — perder a arte é aceitável, sair sem assinatura não.
 */
function drawWordmark(
  pen: Pen,
  img: HTMLImageElement | null,
  x: number,
  y: number,
  o: { align?: 'left' | 'right'; bottom?: boolean } = {},
): void {
  if (img) {
    pen.image(img, x, y, WORDMARK_H, o);
    return;
  }
  pen.text('PinTrack', x, y, {
    size: 15,
    family: DISPLAY,
    color: WORDMARK,
    align: o.align,
    baseline: o.bottom ? 'alphabetic' : 'top',
  });
}

function drawImagery(
  ctx: CanvasRenderingContext2D,
  image: HTMLCanvasElement,
  format: ShareFormat,
  spec: FormatSpec,
): void {
  const k = spec.width / spec.ref;

  ctx.save();
  // The canvas's grayscale recipe. `ctx.filter` is unsupported in older Safari, where it is
  // ignored and the aerial reads a little warmer — a degradation, not a break.
  ctx.filter = 'grayscale(1) contrast(1.3) brightness(1.06)';

  if (format === 'square') {
    // `overlay` on the brand green, as the canvas specifies: it darkens the greens and lifts
    // the paths, which is what turns the aerial into a texture rather than a photograph.
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = 0.5;
    // left 34%, top -46, width 104% of the card.
    drawCover(ctx, image, 0.34 * spec.width, -46 * k, 1.04 * spec.width, 1.04 * spec.width * (1950 / 1738));
  } else {
    /*
     * `screen`, not the `overlay` the canvas also uses here. Deliberate: overlay on a dark
     * backdrop is `2 × backdrop × source`, and this card's backdrop is #0B110D (luminance
     * 0.043), so the product cannot exceed ~0.07 — the aerial is not dimmed, it is
     * mathematically erased. The recipe works on the square because that card sits on
     * #14472F; carried onto near-black it cannot. `screen` keeps the intent the canvas
     * states for both cards — "a silhueta do campo é o gráfico" — at the same low opacity.
     */
    ctx.globalCompositeOperation = 'screen';
    ctx.globalAlpha = 0.6;
    // Anchored to the bottom 62%, looking a third of the way down the frame.
    const h = spec.height * 0.62;
    drawCover(ctx, image, -0.06 * spec.width, spec.height - h, 1.12 * spec.width, h, 0.34);
    ctx.restore();

    /*
     * Fade the aerial's top edge back into the background over the first eighth of it.
     * The canvas crops the image hard at 62% and relies on the veil to hide the join, which
     * worked while `overlay` was keeping it near-black. Now that the terrain actually reads,
     * texture appearing all at once is legible as a seam even though the measured luminance
     * only steps by ~7/255 across it.
     */
    ctx.save();
    const top = spec.height - h;
    const fade = ctx.createLinearGradient(0, top, 0, top + h * 0.125);
    fade.addColorStop(0, spec.background);
    fade.addColorStop(1, 'rgba(11,17,13,0)');
    ctx.fillStyle = fade;
    ctx.fillRect(0, top, spec.width, h * 0.125);
  }
  ctx.restore();

  // The veil, so the type never has to compete with whatever the aerial happens to show.
  ctx.save();
  const grad =
    format === 'square'
      ? gradientAtAngle(ctx, 96, spec.width, spec.height)
      : ctx.createLinearGradient(0, 0, 0, spec.height);

  if (format === 'square') {
    grad.addColorStop(0.1, '#14472F');
    grad.addColorStop(0.4, 'rgba(20,71,47,.94)');
    grad.addColorStop(0.78, 'rgba(20,71,47,.5)');
    grad.addColorStop(1, 'rgba(20,71,47,.3)');
  } else {
    grad.addColorStop(0.3, 'rgba(11,17,13,.9)');
    grad.addColorStop(1, 'rgba(11,17,13,.25)');
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, spec.width, spec.height);
  ctx.restore();
}

/** CSS `linear-gradient(Ndeg, …)` in canvas terms: 0deg points up, angles run clockwise. */
function gradientAtAngle(
  ctx: CanvasRenderingContext2D,
  degrees: number,
  width: number,
  height: number,
): CanvasGradient {
  const rad = ((degrees - 90) * Math.PI) / 180;
  const cx = width / 2;
  const cy = height / 2;
  const len = Math.abs(width * Math.cos(rad)) + Math.abs(height * Math.sin(rad));
  const dx = (Math.cos(rad) * len) / 2;
  const dy = (Math.sin(rad) * len) / 2;
  return ctx.createLinearGradient(cx - dx, cy - dy, cx + dx, cy + dy);
}

function drawSquare(
  pen: Pen,
  data: ShareCardData,
  spec: FormatSpec,
  wordmark: HTMLImageElement | null,
): void {
  const REF = spec.ref;
  const padX = 32;
  const padY = 34;
  const contentWidth = REF - padX * 2;
  const bottom = REF - padY;

  pen.text(`${data.dateLabel} · ${data.teeLabel}`, padX, padY, {
    size: 11,
    color: MUTED,
    tracking: 0.16,
  });

  pen.paragraph(data.courseName, padX, padY + 13 + 7, 250, 27 * 1.15, {
    size: 27,
    family: DISPLAY,
  });

  // ---- bottom-anchored block, laid out upwards from the card's floor
  const statLabelSize = 9.5;
  const statValueSize = 22;
  const statsTop = bottom - (statLabelSize * 1.2 + 1 + statValueSize * 1.2);
  const dividerY = statsTop - 16;

  /*
   * The strip is laid out between the score and the stats, so it pushes the score up rather
   * than overlapping it. With no strip the score keeps the floor it had before, which is what
   * makes an unscored round degrade to the old card instead of to a hole.
   */
  const rows = stripRows(data.holes);
  const stripTop = dividerY - SQUARE_STRIP.marginTop - stripHeight(SQUARE_STRIP, rows);
  const scoreBaseline = (rows.length ? stripTop : dividerY) - 26;

  /*
   * 84, not the 104 this card used to draw. The canvas shrank the hero — and the title with it,
   * 30 → 27 — precisely to seat the strip; kept at 104 the number would collide with the
   * course name on any two-line course.
   */
  pen.text(data.strokes == null ? '—' : `${data.strokes}`, padX, scoreBaseline, {
    size: 84,
    weight: 900,
    baseline: 'alphabetic',
  });

  const toPar = toParLabel(data.toPar);
  if (toPar) {
    const scoreWidth = pen.measure(data.strokes == null ? '—' : `${data.strokes}`, {
      size: 84,
      weight: 900,
    });
    pen.text(toPar, padX + scoreWidth + 14, scoreBaseline - 11, {
      size: 23,
      weight: 700,
      color: GOLD,
      baseline: 'alphabetic',
    });
  }

  drawStrip(pen, padX, stripTop, contentWidth, rows, SQUARE_STRIP);

  pen.rect(padX, dividerY, contentWidth, 1, 'rgba(244,243,238,.22)');

  // Four columns at flex 1 / 1 / 1 / 1.2, as the canvas lays them out.
  const units = 4.2;
  const colWidth = contentWidth / units;
  const cols: Array<[string, string]> = [
    ['Putts', statValue(data.putts)],
    ['GIR', statValue(data.girPct, '%')],
    ['FW', statValue(data.fairwayPct, '%')],
  ];

  cols.forEach(([label, value], i) => {
    const x = padX + i * colWidth;
    pen.text(label.toUpperCase(), x, statsTop, { size: statLabelSize, color: MUTED, tracking: 0.12 });
    pen.text(value, x, statsTop + statLabelSize * 1.2 + 1, { size: statValueSize, weight: 700 });
  });

  drawWordmark(pen, wordmark, REF - padX, bottom, { align: 'right', bottom: true });
}

function drawStory(
  pen: Pen,
  data: ShareCardData,
  spec: FormatSpec,
  wordmark: HTMLImageElement | null,
): void {
  const REF = spec.ref;
  const refHeight = spec.ref * (spec.height / spec.width);
  const padX = 26;
  const padY = 30;
  const contentWidth = REF - padX * 2;
  const bottom = refHeight - padY;

  drawWordmark(pen, wordmark, padX, padY);

  // The wordmark, then 1i's 30 of air. Was 18 + 38, which put the eyebrow 9 lower than drawn.
  const eyebrowY = padY + WORDMARK_H + 30;
  pen.text(`${data.dateLabel} · ${data.teeLabel}`, padX, eyebrowY, {
    size: 10,
    color: MUTED,
    tracking: 0.16,
  });

  const titleY = eyebrowY + 12 + 8;
  const titleLines = pen.paragraph(data.courseName, padX, titleY, contentWidth, 24 * 1.2, {
    size: 24,
    family: DISPLAY,
  });

  // 74, down from 88 — the canvas shrank the story hero to seat the strip, as it did on 1i's
  // square card.
  const scoreBaseline = titleY + titleLines * 24 * 1.2 + 24 + 74 * 0.84;
  pen.text(data.strokes == null ? '—' : `${data.strokes}`, padX, scoreBaseline, {
    size: 74,
    weight: 900,
    baseline: 'alphabetic',
  });

  const toPar = toParLabel(data.toPar);
  if (toPar) {
    const scoreWidth = pen.measure(data.strokes == null ? '—' : `${data.strokes}`, {
      size: 74,
      weight: 900,
    });
    pen.text(toPar, padX + scoreWidth + 10, scoreBaseline - 9, {
      size: 18,
      weight: 700,
      color: GOLD,
      baseline: 'alphabetic',
    });
  }

  // ---- bottom-anchored stats, with the strip stacked directly above them
  const statLabelSize = 9;
  const statValueSize = 19;
  const statsTop = bottom - (statLabelSize * 1.2 + statValueSize * 1.2);
  const statsDividerY = statsTop - 14;

  const rows = stripRows(data.holes);
  drawStrip(
    pen,
    padX,
    statsDividerY - STORY_STRIP.marginTop - stripHeight(STORY_STRIP, rows),
    contentWidth,
    rows,
    STORY_STRIP,
  );

  pen.rect(padX, statsDividerY, contentWidth, 1, 'rgba(244,243,238,.18)');

  const cols: Array<[string, string]> = [
    ['Putts', statValue(data.putts)],
    ['GIR', statValue(data.girPct, '%')],
    ['FW', statValue(data.fairwayPct, '%')],
    ['SD', data.differential == null ? '—' : fmt1(data.differential)],
  ];

  const colWidth = contentWidth / cols.length;
  cols.forEach(([label, value], i) => {
    const x = padX + i * colWidth;
    pen.text(label.toUpperCase(), x, statsTop, { size: statLabelSize, color: MUTED, tracking: 0.12 });
    pen.text(value, x, statsTop + statLabelSize * 1.2, { size: statValueSize, weight: 700 });
  });
}

// -------------------------------------------------------------------- fonts

/**
 * Canvas text uses whatever the font stack resolves to *at draw time*, with no reflow when a
 * webfont arrives later. Drawing before Archivo and Newsreader are ready produces a card set
 * in Helvetica and Times, silently and permanently.
 */
async function ensureFonts(): Promise<void> {
  if (!document.fonts) return;
  const faces = [
    `900 84px ${UI}`,
    // The strip's segment totals. Archivo is variable, but the browser still has to be asked
    // for each weight it will be told to render — an unloaded 800 falls back to 700 silently.
    `800 14px ${UI}`,
    `700 22px ${UI}`,
    `500 11px ${UI}`,
    `400 11px ${UI}`,
    `400 27px ${DISPLAY}`,
  ];
  try {
    await Promise.all(faces.map((f) => document.fonts.load(f)));
    await document.fonts.ready;
  } catch {
    // A font that refuses to load is not a reason to refuse the card.
  }
}

// ------------------------------------------------------------------ export

export function shareFileName(courseName: string, dateLabel: string, format: ShareFormat): string {
  const slug = courseName
    .normalize('NFD')
    // Strip the combining marks NFD just split off, so "Itanhangá" slugs as "itanhanga".
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const date = dateLabel.replace(/\s+/g, '-').toLowerCase();
  return `myrangefinder-${slug || t('share.fileFallback')}-${date}-${format}.png`;
}

/**
 * PNG bytes for a rendered card.
 *
 * Throws only if the canvas itself refuses. A *tainted* canvas is caught here and reported
 * as such so the caller can redraw without imagery — that is the one failure mode where
 * there is still a good card to be had.
 */
export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    } catch (err) {
      reject(err instanceof Error ? err : new Error(t('share.exportFailed')));
    }
  });
}
